import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Project} from '../../classes/project/project';
import {StatesService} from '../state/states.service';
import {ProjectUser} from '../../classes/project-user/project-user';
import {ProjectDeviceLocation} from '../../classes/project-device-location/project-device-location';
import {ProjectDeviceAssignement} from '../../classes/project-device-assignement/project-device-assignement';
import {Observable} from 'rxjs';
import {map} from 'rxjs/internal/operators';
import {isObject} from 'util';
import {Plan} from '../../classes/plan/plan';
import {LocationPlan} from '../../classes/plan/location-plan';


@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  constructor(private http: HttpClient,
              private statesService: StatesService) {
  }

  getProjects(active: boolean, level: number = -1, isSafe: boolean = false) {
    const activeParam = 'active=' + (active ? 1 : 0);
    const projectLevel = level > -1 ? level : this.statesService.stateLevel;
    return this.http.get(`projects?` + (isSafe ? '' : activeParam) + `&level=` + projectLevel);
  }

  getProjectsForSubscription(active: boolean) {
    const activeParam = 'active=' + (active ? 1 : 0);
    return this.http.get(`projects?` + activeParam + `&level=` + this.statesService.stateLevel).pipe(map((data: any) => {
        data.forEach((d) => {
          if (isObject(d.project_status_abo_maths)) {
            d.subscription_maths_start_date = d.project_status_abo_maths.dateStart;
            d.subscription_maths_end_date = d.project_status_abo_maths.dateEnd;
            d.corr_therm_id = d.project_status_abo_maths.state;
          }
          if (isObject(d.project_status_third_party_data) && !Array.isArray(d.project_status_third_party_data)) {
            d.senceive_id = d.project_status_third_party_data.senceive_subscription_id !== undefined ? d.project_status_third_party_data.senceive_subscription_id : 0;
            d.csv_subscription_id = d.project_status_third_party_data.csv_subscription_id !== undefined ? d.project_status_third_party_data.csv_subscription_id : 0;
            d.newsteo_id = d.project_status_third_party_data.newsteo_subscription_id !== undefined ? d.project_status_third_party_data.newsteo_subscription_id : 0;
            d.eventdyn_id = d.project_status_third_party_data.eventdyn_subscription_id !== undefined ? d.project_status_third_party_data.eventdyn_subscription_id : 0;
          } else {
            d.senceive_id = 0;
            d.csv_subscription_id = 0;
            d.newsteo_id = 0;
            d.eventdyn_id = 0;
          }
        });
        return data;

      })
    );
  }

  getProject(idProject: number, group: string = null) {
    group = group !== null ? `,` + group : '';
    return this.http.get(`projects/` + idProject + `?groups=level` + group);
  }

  getProjectNoteUser(idProject: number, group: string = null) {
    group = group !== null ? `,` + group : '';
    return this.http.get<any>(`projects/` + idProject + `?groups=level` + group).pipe(
      map(project => {
        if (project && project.note_project_users) {
          project.note_project_users = project.note_project_users.map(note => {
            if (note.user) {
              note.note_user_name = `${note.user.firstname} ${note.user.lastname}`.trim();
            }
            return note;
          });
        }
        return project.note_project_users.reverse();
      })
    );
  }


  postProject(project: Project) {
    return this.http.post(`projects`, project);
  }

  patchProject(idProject: number, project: Project) {
    return this.http.patch(`projects/` + idProject, project);
  }

  deleteProject(idProject: number) {
    return this.http.delete(`projects/` + idProject);
  }

  getProjectUsers(idProject: number) {
    return this.http.get(`projects/` + idProject + `users`);
  }

  postProjectUser(idProject: number, projectUser: ProjectUser) {
    return this.http.post(`projects/` + idProject + `/user`, projectUser);
  }

  patchProjectUser(idProjectUser: number, projectUser: ProjectUser) {
    return this.http.patch(`project-users/` + idProjectUser, projectUser);
  }

  deleteProjectUser(idProjectUser: number) {
    return this.http.delete(`project-users/` + idProjectUser + `?hard-delete=true`);
  }

  postProjectPlan(planFormData: FormData) {
    return this.http.post(`project-plans`, planFormData);
  }

  getProjectLocations(projectId) {
    return this.http.get(`projects/${projectId}?groups=locations,notations`);
  }

  getProjectDeviceLocation(idProjectDeviceLocation: number) {
    return this.http.get(`project-device-locations/` + idProjectDeviceLocation);
  }

  postProjectDeviceLocation(idProject: number, projectDeviceLocation: ProjectDeviceLocation) {
    return this.http.post(`projects/` + idProject + `/device-locations`, projectDeviceLocation);
  }

  patchProjectDeviceLocation(idProjectDeviceLocation: number, projectDeviceLocation: ProjectDeviceLocation) {
    return this.http.patch(`project-device-locations/` + idProjectDeviceLocation, projectDeviceLocation);
  }

  deleteProjectDeviceLocation(idProjectDeviceLocation: number, logic: boolean = true) {
    return this.http.delete(`project-device-locations/` + idProjectDeviceLocation);
  }

  getProjectDeviceAssignements(idProject: number) {
    return this.http.get(`projects/` + idProject + `/device-assignements`);
  }

  getProjectDeviceAssignement(idProjectDeviceAssignement: number) {
    return this.http.get(`project-device-assignements/` + idProjectDeviceAssignement);
  }

  postProjectDeviceAssignement(idProject: number, projectDeviceAssignment: ProjectDeviceAssignement, generateLocation?: boolean) {
    const queryString = 'boolean' === typeof generateLocation ? '?generate-location=' + generateLocation : '';
    return this.http.post(`projects/` + idProject + `/device-assignements` + queryString, projectDeviceAssignment);
  }

  patchProjectDeviceAssignement(idProjectDeviceAssignement: number, projectDeviceAssignment: ProjectDeviceAssignement) {
    return this.http.patch(`project-device-assignements/` + idProjectDeviceAssignement, projectDeviceAssignment);
  }

  postEvent(projectId, event: Event): Observable<any> {
    return this.http.post(`projects/${projectId}/events`, event);
  }

  patchEvent(eventId, event: Event): Observable<any> {
    return this.http.patch(`events/${eventId}`, event);
  }

  deleteProjectDeviceAssignement(idProjectDeviceAssignement: number, logic: boolean = true) {
    const deleteType = logic ? '' : '?hard-delete=true';
    return this.http.delete(`project-device-assignements/` + idProjectDeviceAssignement + deleteType);
  }

  postProjectFile(idProject: number, formData: FormData) {
    return this.http.post(`projects/` + idProject + `/file`, formData);
  }

  deleteProjectFile(idFile: number) {
    return this.http.delete('project-files/' + idFile + '?hard-delete=true');
  }

  getProjectEdasAlarms(projectId: number) {
    return this.http.get('projects/' + projectId + '/edas-alarms');

  }

  postLocationPlan(locationPlan: LocationPlan) {
    return this.http.post(`locations-plan`, locationPlan);
  }

  patchLocationPlan(idLocationPlan: number, locationPlan: LocationPlan) {
    return this.http.patch(`locations-plan/` + idLocationPlan, locationPlan);
  }

  deleteLocationPlan(idLocationPlan: number, logic: boolean = true) {
    const deleteType = logic ? '' : '?hard-delete=true';
    return this.http.delete(`locations-plan/` + idLocationPlan + deleteType);
  }

  patchProjectPlan(idPlan: number, plan: Plan) {
    return this.http.patch(`project-plans/` + idPlan, plan);
  }

  deleteProjectPlan(idPlan: number, logic: boolean = true) {
    const deleteType = logic ? '' : '?hard-delete=true';
    return this.http.delete(`project-plans/` + idPlan + deleteType)
  }

  getSubscriptionDetails(subscriptionId: number, subscriptionType: string = 'wimd_subscription') {
    return this.http.get('subscriptions/' + subscriptionType + '/settings/' + subscriptionId);
  }
}
